import { SpinResponse } from '@cgs/data-access';
import { Container, Vector2 } from '@cgs/wge';
import { IconModel } from '../../game/components/icon_model';
import { ComponentIndex } from '../entities_engine/component_index';
import { EntitiesEngine } from '../entities_engine/entities_engine';
import { Entity } from '../entities_engine/entity';
import { BrakingCalculationInfo } from '../entity_components/braking_calculation_info';
import { ComponentNames } from '../entity_components/component_names';
import { FreeFallBeforeBrakingSystem } from '../free_fall_reels_engine/systems/free_fall_before_braking_system';
import { IReelsConfig } from '../i_reels_config';
import { IconEnumerator } from '../icon_enumerator';
import { IconsSceneObject } from '../icons_scene_object';
import { Easing } from '../internal_reels_config';
import { ReelsSoundModel } from '../reels_sound_model';
import { GameStateMachine } from '../state_machine/game-state-machine';
import { MultiSceneReelsEngine } from '../multi_scene_reels_engine';

export class CollapseReelsEngine extends MultiSceneReelsEngine {
  public hiddenAnimationEntities: Entity[][];
  public hiddenSoundEntities: Entity[][];

  private readonly _reelIndex: ComponentIndex;
  private readonly _lineIndex: ComponentIndex;
  private readonly _positionIndex: ComponentIndex;
  private readonly _toRemoveIndex: ComponentIndex;

  constructor(
    container: Container,
    entityEngine: EntitiesEngine,
    iconRender: IconsSceneObject,
    animIconRender: IconsSceneObject,
    iconModel: IconModel,
    enumerator: IconEnumerator,
    reelConfig: IReelsConfig,
    gameStateMachine: GameStateMachine<SpinResponse>,
    reelsSoundModel: ReelsSoundModel,
    useSounds: boolean
  ) {
    super(
      container,
      entityEngine,
      iconRender,
      animIconRender,
      iconModel,
      enumerator,
      reelConfig,
      gameStateMachine,
      reelsSoundModel,
      useSounds,
      [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
      'alpha'
    );
    this.initHiddenEntities();

    this._reelIndex = entityEngine.getComponentIndex(ComponentNames.ReelIndex);
    this._lineIndex = entityEngine.getComponentIndex(ComponentNames.LineIndex);
    this._positionIndex = entityEngine.getComponentIndex(ComponentNames.Position);
    this._toRemoveIndex = entityEngine.getComponentIndex(ComponentNames.ToRemoveIndex);
  }

  public createSystems(): void {
    super.createSystems();

    const indexesForFilter: ComponentIndex[] = [
      this.entityEngine.getComponentIndex(ComponentNames.BrakingCalculationInfo),
      this.entityEngine.getComponentIndex(ComponentNames.ReelIndex),
      this.entityEngine.getComponentIndex(ComponentNames.FreeFallingIcon),
    ];
    const freeFallBrakingSystem = new FreeFallBeforeBrakingSystem(
      this.entityEngine,
      this.entityCacheHolder,
      this.internalConfig,
      this.iconsEnumerator,
      indexesForFilter
    ).withInitialize();

    freeFallBrakingSystem.updateOrder = this.beforeBrakingSystem.updateOrder - 10;
    freeFallBrakingSystem.register();
  }

  public stopEntity(reel: number, line: number, winIndex: number): void {
    this.iconsEnumerator.setWinIndex(reel, line, winIndex);
    this.commandSystem.stopEntity(reel, line);
  }

  public hideAnimEntity(entity: Entity): void {
    const reel = entity.get<number>(this.entityEngine.getComponentIndex(ComponentNames.ReelIndex));
    if (!entity.get(this._toRemoveIndex)) {
      this.hiddenAnimationEntities[reel].push(entity);
    }

    entity.addComponent(ComponentNames.Speed, Vector2.Zero.clone());
    entity.addComponent(ComponentNames.RelocatedFlag, 1);
    entity.addComponent(ComponentNames.FreeFallingIcon, true);
    const position = entity.get<Vector2>(this._positionIndex);
    position.y = this.internalConfig.offset.y - this.internalConfig.symbolSize.y;
    entity.set(this._positionIndex, position);
    entity.set(this._lineIndex, -1);
  }

  public hideSoundEntity(entity: Entity): void {
    const reel = entity.get<number>(this.entityEngine.getComponentIndex(ComponentNames.ReelIndex));
    if (!entity.get(this._toRemoveIndex)) {
      this.hiddenSoundEntities[reel].push(entity);
    }

    entity.addComponent(ComponentNames.Speed, Vector2.Zero.clone());
    entity.addComponent(ComponentNames.RelocatedFlag, 1);
    entity.addComponent(ComponentNames.FreeFallingIcon, true);

    const position = entity.get<Vector2>(this._positionIndex);
    position.y = this.internalConfig.offset.y - this.internalConfig.symbolSize.y;
    entity.set(this._positionIndex, position);
    entity.set(this._lineIndex, -1);
  }

  public moveEntityTo(
    entity: Entity,
    toLine: number,
    speed: Vector2,
    duration: number,
    easing: Easing
  ): void {
    const line = entity.get<number>(this._lineIndex);
    if (line >= 0 && line < this.ReelConfig.lineCount) {
      this.stablePositions[entity.get<number>(this._reelIndex)][line] = false;
    }
    if (toLine >= 0 && toLine < this.ReelConfig.lineCount) {
      this.stablePositions[entity.get<number>(this._reelIndex)][toLine] = false;
    }

    const position = entity.get<Vector2>(this._positionIndex);
    const endSpeed = this.calculateSpeed(speed);
    entity.set(this._lineIndex, toLine);
    entity.addComponent(
      ComponentNames.BrakingCalculationInfo,
      new BrakingCalculationInfo(position, endSpeed)
    );
    entity.addComponent(ComponentNames.FreeFallingIcon, true);
    this.commandSystem.accelerateEntity(entity, Vector2.Zero.clone(), endSpeed, duration, easing);
  }

  public slotCollapsed(): boolean {
    return (
      this.stablePositions.filter((reelPos) => reelPos.filter((p) => !p).length !== 0).length === 0
    );
  }

  public initHiddenEntities(): void {
    this.hiddenAnimationEntities = Array.from({ length: this.internalConfig.reelCount }, () => []);
    this.hiddenSoundEntities = Array.from({ length: this.internalConfig.reelCount }, () => []);
  }
}
